import { TimezoneItem } from './models'
import { INITIAL_FORMATTING_OPTIONS, TIMEZONE_LS_TOKEN, TIMEZONE_OFFSET_MAP } from './constants'

export const getInitialTimezoneItem = (): TimezoneItem => {
  const fallbackTimezoneItem = {
    regions: new Set(),
    pickTimezone: INITIAL_FORMATTING_OPTIONS.timeZone,
    timezoneOffsetName: formatTimezoneOffsetName(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: INITIAL_FORMATTING_OPTIONS.timeZone,
        timeZoneName: 'longOffset',
      })
        .format(new Date(0))
        .split(' ')[1]
    ),
    zones: new Set(),
    offsetMs: 0,
    offsetMsRelative: 0,
  } satisfies TimezoneItem
  const restoredTimezoneItem = JSON.parse(localStorage.getItem(TIMEZONE_LS_TOKEN) ?? 'null')

  return (
    TIMEZONE_OFFSET_MAP[
      restoredTimezoneItem?.timezoneOffsetName ?? fallbackTimezoneItem.timezoneOffsetName
    ] ?? fallbackTimezoneItem
  )
}

export function calcTimezoneOffset(timezoneNamesArr: string[]): Record<string, string> {
  const map = Object.fromEntries(
    timezoneNamesArr.map((tzName) => [
      tzName,
      formatTimezoneOffsetName(
        new Intl.DateTimeFormat('en-GB', { timeZone: tzName, timeZoneName: 'longOffset' })
          .format(new Date(0))
          .split(' ')[1]
      ),
    ])
  )

  return map
}

export function calcTimezoneOffsetMap(
  timezoneNamesArr: string[],
  initialTimezoneResolvedObject: Intl.ResolvedDateTimeFormatOptions,
  abbreviationsMap: Record<string, string[]>
): [{ [timezoneName: string]: TimezoneItem }, TimezoneItem | undefined] {
  const resultMap: Record<string, TimezoneItem> = {}
  let initialTimezoneObject: TimezoneItem | undefined

  timezoneNamesArr.forEach((tzName) => {
    const tzOffsetName = getTimezoneOffsetName(tzName)

    if (!resultMap[tzOffsetName]) {
      const { offsetMs, offsetMsRelative } = getUTCComparableDateByTimezoneName(tzOffsetName)

      resultMap[tzOffsetName] = {
        regions: new Set(),
        zones: new Set(),
        pickTimezone: tzName,
        timezoneOffsetName: tzOffsetName,
        offsetMs,
        offsetMsRelative,
      }
    }
    resultMap[tzOffsetName].regions.add(tzName.split('/')[0] ?? tzName)
    resultMap[tzOffsetName].zones.add(tzName)

    if (tzName === initialTimezoneResolvedObject.timeZoneName) {
      initialTimezoneObject = resultMap[tzOffsetName]
    }
  })

  Object.keys(resultMap).forEach((timezoneOffsetName) => {
    const tz = resultMap[timezoneOffsetName]
    const abbreviations = abbreviationsMap[tz.timezoneOffsetName]
    if (abbreviations && abbreviations.length > 0) {
      tz.abbreviations = abbreviations
    }
  })

  return [resultMap, initialTimezoneObject]
}

function getUTCComparableDateByTimezoneName(timeString: string /* +10:00 */) {
  const [hours, minutes] = timeString
    .slice(1) /* remove "+"*/
    .split(':')
    .map((v) => +v)
  if (isNaN(hours) || isNaN(minutes))
    throw new Error("Can't parse hours and minutes from " + timeString)

  const direction: 1 | -1 = timeString.startsWith('+') ? 1 : -1
  const offsetMs = new Date(Date.UTC(1970, 0, 1, hours, minutes)).getTime() * direction
  const offsetMsRelative = offsetMs - getOffsetOfBrowserTimezoneMs()

  return { offsetMs, offsetMsRelative }
}

function getOffsetOfBrowserTimezoneMs() {
  const currentOffsetMin = new Date().getTimezoneOffset()
  /* 
    Why multiply by -1? We use offset negative to what browser provides.
    
    Browser: timezone = +03:00; offset = -180 (min)
    Our app: timezone = +03:00; offset = 10800000 (ms) e.g. 180 (min)

    TODO: Fix this
  */
  return currentOffsetMin * 60 * 1000 * -1
}

/** GMT+XX:XX > UTC+XX:XX */
function formatTimezoneOffsetName(offset: string) {
  if (offset === 'GMT') return '+00:00'
  return offset.replace('GMT', '')
}

function getDateFormatter(timezoneName: string) {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezoneName,
    timeZoneName: 'longOffset',
  })
}

function getTimezoneOffsetName(timezoneName: string) {
  const currentFormatter = getDateFormatter(timezoneName)
  return /* GMT+XX:XX */ formatTimezoneOffsetName(currentFormatter.format(new Date()).split(' ')[1])
}
